import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

const QuarterlyRangePicker = (props) => {
    const {
        selectedReportingPeriod,
        setSelectedReportingPeriod,
        setEnableResetFilters,
        setApplyFilter,
        reportingPeriodOptions,
    } = props;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

    const currentQuarter = (() => {
        if (currentMonth < 3) return "Q1";
        if (currentMonth < 6) return "Q2";
        if (currentMonth < 9) return "Q3";
        return "Q4";
    })();

    const quarterOrder = {
        Q1: 1,
        Q2: 2,
        Q3: 3,
        Q4: 4,
    };

    const [isOpenReportingPeriod, setIsOpenReportingPeriod] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);

    const dropdownRef = useRef(null);
    const daterangeRef = useRef(null);

    const [fromQuarter, setFromQuarter] = useState(null);
    const [toQuarter, setToQuarter] = useState(null);
    const [fromYear, setFromYear] = useState(null);
    const [toYear, setToYear] = useState(null);
    const [isSelectingYear, setIsSelectingYear] = useState({
        from: false,
        to: false,
    });

    const [selectedYear, setSelectedYear] = useState({
        from: null,
        to: null,
    });
    const [activePicker, setActivePicker] = useState();

    const yearsList = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i);

    const toggleDropdown = () => {
        setShowCalendar(false);
        setIsOpenReportingPeriod((prev) => !prev);
    };

    const resetRangeSelection = () => {
        setFromQuarter(null);
        setToQuarter(null);
        setFromYear(null);
        setToYear(null);
        setSelectedYear({ from: null, to: null });
        setActivePicker();
        setIsSelectingYear({ from: false, to: false });
    };

    const handleReportingPeriod = (option) => {
        if (option.name === "Custom range") {
            setIsOpenReportingPeriod(false);
            setShowCalendar(true);
            resetRangeSelection();
        } else {
            let startYear = 2021;
            let endYear = 2025;

            // Extract years from effectiveStartDt and effectiveEndDt if available
            if (option.value?.effectiveStartDt) {
                startYear = new Date(
                    option.value.effectiveStartDt
                ).getFullYear();
            }
            if (option.value?.effectiveEndDt) {
                endYear = new Date(option.value.effectiveEndDt).getFullYear();
            }

            setSelectedReportingPeriod({
                ...option,
                startYear: startYear,
                startQuarter: "1",
                endYear: endYear,
                endQuarter: "4",
            });
            setEnableResetFilters(true);
            setApplyFilter(true);
            setIsOpenReportingPeriod(false);
            setShowCalendar(false);
            resetRangeSelection();
        }
    };

    const handleClickOutside = (event) => {
        if (
            !dropdownRef.current?.contains(event.target) &&
            !daterangeRef.current?.contains(event.target)
        ) {
            setIsOpenReportingPeriod(false);
            setShowCalendar(false);
            resetRangeSelection();
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleQuarterClick = (q) => {
        const picker = activePicker;
        const year = selectedYear[picker];

        if (!year) return;

        let newFromQuarter = fromQuarter;
        let newFromYear = fromYear;
        let newToQuarter = toQuarter;
        let newToYear = toYear;

        // Helper to Compare Quarters
        const getVal = (quarter, yr) => quarterToNumber(quarter, yr);

        if (picker === "from") {
            const formVal = getVal(q, year);
            const toVal = getVal(toQuarter, toYear);

            if (toQuarter && toVal < formVal) {
                newToQuarter = q;
                newToYear = year;
            } else {
                newFromQuarter = q;
                newFromYear = year;
            }
           
            setActivePicker("to");
            setIsSelectingYear((prev) => ({
                ...prev,
                to: true,
            }));
        } else if (picker === "to") {
              const fromVal = getVal(fromQuarter, fromYear);
              const toVal = getVal(q, year);
              
              if (toVal < fromVal) {
                newFromQuarter = q;
                newFromYear = year;
              }else {
                newToQuarter = q;
                newToYear = year;
              }
        }

        // Apply Updates
        setFromQuarter(newFromQuarter);
        setFromYear(newFromYear);
        setToQuarter(newToQuarter);
        setToYear(newToYear);
    };

    // Converts quarter/year to comparable number (e.g., Q2 2023 -> 2023.25)
    const quarterToNumber = (quarter, year) => {
        const quarterIndex = QUARTERS.indexOf(quarter);
        return year + quarterIndex * 0.25;
    };

    const handleConfirm = () => {
        const getQuarterNumber = (q) => q.replace("Q", "");

        setSelectedReportingPeriod({
            name: "range",
            displayName: `${fromQuarter} ${fromYear} to ${toQuarter} ${toYear}`,
            startYear: fromYear,
            startQuarter: getQuarterNumber(fromQuarter),
            endYear: toYear,
            endQuarter: getQuarterNumber(toQuarter),
        });

        setShowCalendar(false);
        setApplyFilter(true);
        setEnableResetFilters(true);
        resetRangeSelection();
    };

    const handleBack = () => {
        setIsOpenReportingPeriod(true);
        setShowCalendar(false);
        setActivePicker("from");
        setFromQuarter("");
        setFromYear("");
        setToQuarter("");
        setToYear("");
    };

    const handleYearBack = () => {
        setIsSelectingYear((prev) => ({
            ...prev,
            [activePicker]: true,
        }));
    };

    const handlefromclick = () => {
        setActivePicker("from");
        setIsSelectingYear((prev) => ({ ...prev, from: true }));
    };

    const handleYearClick = (year) => {
        setSelectedYear((prev) => ({
            ...prev,
            [activePicker]: year,
        }));

        if (activePicker === "from") {
            // After selecting From year, show From quarter picker
            setIsSelectingYear((prev) => ({
                ...prev,
                from: false,
            }));
        } else if (activePicker === "to") {
            // After selecting To year, show To quarter picker
            setIsSelectingYear((prev) => ({
                ...prev,
                to: false,
            }));
        }
    };

    return (
        <div className="col-lg-3 col-md-6 col-sm-4 col-6 col-print">
            <label className="header pb-0" htmlFor="reporting-period">
                Reporting period
            </label>

            <div className="reporting-period" ref={dropdownRef}>
                <div
                    role="button"
                    tabIndex={0}
                    onClick={toggleDropdown}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleDropdown();
                        }
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={isOpenReportingPeriod}
                    className="reportTableFilter"
                >
                    <span>
                        {selectedReportingPeriod?.displayName ||
                            "Select Reporting Period"}
                    </span>
                    <i
                        className={`fa-solid fa-angle-${
                            isOpenReportingPeriod ? "up" : "down"
                        }`}
                        aria-hidden="true"
                    />
                </div>

                {isOpenReportingPeriod && !showCalendar && (
                    <ul className="reportingPeriod-dropdown" role="listbox">
                        {reportingPeriodOptions.map((option) => (
                            <li
                                key={option.name}
                                className={`dropdown-option ${
                                    option.name ===
                                    selectedReportingPeriod?.name
                                        ? "option-selected"
                                        : ""
                                }`}
                                role="option"
                                onClick={() => handleReportingPeriod(option)}
                                tabIndex={0}
                            >
                                {option.name}
                            </li>
                        ))}
                    </ul>
                )}

                {showCalendar && (
                    <dialog
                        className="reportingPeriod-dropdown range-picker"
                        ref={daterangeRef}
                        open={showCalendar}
                        aria-label="range picker"
                    >
                        <div className="range-header">
                            <button
                                type="button"
                                className="range-button"
                                aria-label="Go back" // descriptive label for screen readers
                                onClick={handleBack}
                            >
                                <i
                                    className="fa-solid fa-chevron-left"
                                    aria-hidden="true"
                                ></i>
                            </button>

                            <span>Range</span>
                        </div>

                        {/* From Picker */}
                        <div className="Quarter-input-section mb-1">
                            <div>
                                <label htmlFor="fromPicker">From</label>
                                <button
                                    type="button"
                                    className="quarter-input"
                                    onClick={() => handlefromclick()}
                                    style={{
                                        border:
                                            activePicker === "from"
                                                ? "2px solid #037CB7"
                                                : "",
                                        backgroundColor:
                                            activePicker === "from"
                                                ? "#E6F2F8"
                                                : "#fff",
                                    }}
                                >
                                    {fromQuarter
                                        ? `${fromYear} - ${fromQuarter}`
                                        : selectedYear.from
                                        ? `${selectedYear.from} -`
                                        : "Select"}

                                    {/* <i
                                        className={
                                            "fa-solid fa-angle-down chevron-icon-color"
                                        }
                                        aria-hidden="true"
                                    /> */}
                                </button>
                            </div>
                            <div>
                                <label htmlFor="toPicker">Through</label>
                                <button
                                    type="button"
                                    className="quarter-input"
                                    onClick={() => {
                                        setActivePicker("to");
                                        setIsSelectingYear((prev) => ({
                                            ...prev,
                                            to: true,
                                        }));
                                    }}
                                    style={{
                                        border:
                                            activePicker === "to"
                                                ? "2px solid #037CB7"
                                                : "",
                                        backgroundColor:
                                            activePicker === "to"
                                                ? "#E6F2F8"
                                                : "#fff",
                                    }}
                                >
                                    {toQuarter
                                        ? `${toYear} - ${toQuarter}`
                                        : selectedYear.to
                                        ? `${selectedYear.to} -`
                                        : "Select"}
                                </button>
                            </div>
                        </div>

                        <div style={{ textAlign: "center" }}>
                            {isSelectingYear[activePicker] ? (
                                <span> Select Year</span>
                            ) : null}
                        </div>

                        {/* Active picker UI: either year selection or quarter selection */}
                        {activePicker && (
                            <div
                                style={{
                                    padding: "4px 8px 4px 8px",
                                }}
                            >
                                {/* Show Back button if in quarter selection */}
                                {!isSelectingYear[activePicker] && (
                                    <button
                                        type="button"
                                        className="range-button"
                                        aria-label="Go back to year selection"
                                        onClick={handleYearBack}
                                        style={{
                                            marginBottom: "10px",
                                            background: "none",
                                            border: "none",
                                            color: "#037CB7",
                                            cursor: "pointer",
                                            fontSize: "1rem",
                                        }}
                                    ></button>
                                )}

                                {isSelectingYear[activePicker] ? (
                                    <div
                                        className="d-flex flex-wrap"
                                        style={{
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        {yearsList.map((year) => (
                                            <button
                                                key={year}
                                                className={`quarter-button ${
                                                    year ===
                                                    selectedYear[activePicker]
                                                        ? "quarter-selected"
                                                        : "quarter-unSelected"
                                                }`}
                                                onClick={() =>
                                                    handleYearClick(year)
                                                }
                                                type="button"
                                                style={{ width: "40px" }}
                                            >
                                                {year}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        {" "}
                                        <div
                                            style={{
                                                textAlign: "center",
                                                marginTop: "-37px",
                                            }}
                                        >
                                            {" "}
                                            <button
                                                type="button"
                                                className="range-button"
                                                aria-label="Go back to year selection"
                                                onClick={handleYearBack}
                                                style={{
                                                    marginBottom: "10px",
                                                    background: "none",
                                                    border: "none",
                                                    color: "#037CB7",
                                                    cursor: "pointer",
                                                    fontSize: "1rem",
                                                }}
                                            >
                                                <i
                                                    className="fa-solid fa-chevron-left"
                                                    aria-hidden="true"
                                                />{" "}
                                                {/* Back */}
                                            </button>{" "}
                                            Select Quarter
                                        </div>
                                        <div
                                            className="d-flex flex-wrap"
                                            style={{
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            {QUARTERS.map((q) => {
                                                const year =
                                                    activePicker === "from"
                                                        ? selectedYear.from
                                                        : selectedYear.to;

                                                const isFutureQuarter =
                                                    year > currentYear ||
                                                    (year === currentYear &&
                                                        quarterOrder[q] >=
                                                            quarterOrder[
                                                                currentQuarter
                                                            ]);

                                                const isSelected =
                                                    (activePicker === "from" &&
                                                        q === fromQuarter &&
                                                        fromYear ===
                                                            selectedYear.from) ||
                                                    (activePicker === "to" &&
                                                        q === toQuarter &&
                                                        toYear ===
                                                            selectedYear.to);

                                                return (
                                                    <button
                                                        key={q}
                                                        className={`quarter-button ${
                                                            isSelected
                                                                ? "quarter-selected"
                                                                : "quarter-unSelected"
                                                        } ${
                                                            isFutureQuarter
                                                                ? "quarter-disabled"
                                                                : ""
                                                        }`}
                                                        onClick={() =>
                                                            !isFutureQuarter &&
                                                            handleQuarterClick(
                                                                q
                                                            )
                                                        }
                                                        type="button"
                                                        disabled={
                                                            isFutureQuarter
                                                        }
                                                    >
                                                        {q}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Confirm button */}
                        <div
                            className="fs-16 d-flex confirm-range"
                            style={{ marginTop: "15px" }}
                        >
                            <button
                                type="button"
                                className="btn-primary confirm-report-button"
                                aria-live="polite"
                                onClick={handleConfirm}
                                disabled={!fromQuarter || !toQuarter}
                                style={{
                                    padding: "8px 16px",
                                    fontSize: "1rem",
                                    cursor:
                                        !fromQuarter || !toQuarter
                                            ? "not-allowed"
                                            : "pointer",
                                    opacity:
                                        !fromQuarter || !toQuarter ? 0.5 : 1,
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </dialog>
                )}
            </div>
        </div>
    );
};

QuarterlyRangePicker.propTypes = {
    selectedReportingPeriod: PropTypes.object,
    setSelectedReportingPeriod: PropTypes.func,
    setEnableResetFilters: PropTypes.func,
    setApplyFilter: PropTypes.func,
    reportingPeriodOptions: PropTypes.array,
};

export default QuarterlyRangePicker;
